import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { CatalogCustomer } from "../models/CatalogCustomer";
import { getCatalogCustomers } from "../repositories/CatalogCustomerRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: (value: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
	showAll?: boolean;
};

const ALL_CUSTOMER: CatalogCustomer = {
	CustomerId: -1,
	CustomerName: "All",
	IsActive: true,
	CreatedBy: null,
	CreatedDate: null,
	ModifiedBy: null,
	ModifiedDate: null,
};

const useCustomers = (onError?: (error: Error) => void) => {
	return useQuery<CatalogCustomer[], Error>({
		queryKey: ["catalogCustomers"],
		queryFn: () => getCatalogCustomers(),
		onError: onError,
	});
};

export const CustomerSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
	showAll = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: customers, isLoading } = useCustomers((error) =>
		setError(error.message),
	);

	const options = showAll
		? [ALL_CUSTOMER, ...(customers || [])]
		: customers || [];

	const selectedCustomer =
		showAll && value === null
			? ALL_CUSTOMER
			: customers?.find((c) => c.CustomerId === value) || null;

	return (
		<>
			<Autocomplete
				id="customer-selector"
				fullWidth
				clearOnEscape
				options={options}
				getOptionLabel={(option) => option.CustomerName}
				isOptionEqualToValue={(a, b) => a.CustomerId === b.CustomerId}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedCustomer}
				onChange={(_, newValue) => {
					onChange(
						!newValue || newValue.CustomerId === -1
							? null
							: newValue.CustomerId,
					);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading customers"
			/>
		</>
	);
};
